import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { useRequest } from "ahooks";
import { Divider, Dropdown, Input } from "antd";
import { sify, tify } from "chinese-conv/dist";
import { isEmpty } from "ramda";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import { Icon } from "@/components/icon";
import {
  homeActiveTabState,
  homeSearchIdState,
} from "@/models/learning-record-model";
import { TabEnum } from "@/pages/index-page/constants";
import { getSearchCoursewaresAndTutorialsApi } from "@/services/api/index-page";
import {
  ISearchCoursewareType,
  ISearchDataType,
  ISearchTutorialType,
} from "@/types/index-page";

import styles from "./button.module.less";

export const SearchButton: FC = () => {
  const { t } = useTranslation();

  const setSearchId = useSetRecoilState(homeSearchIdState);

  const setActiveTab = useSetRecoilState(homeActiveTabState);

  const [open, setOpen] = useState(false);

  /** 搜索框内容 */
  const [searchValue, setSearchValue] = useState("");

  /** 是否显示课程下拉 */
  const [showContent, setShowContent] = useState(false);

  /** 搜索結果 */
  const [searchResults, setSearchResults] = useState<ISearchDataType>();

  const { run, loading } = useRequest(getSearchCoursewaresAndTutorialsApi, {
    manual: true,
    onSuccess: ({ data }) => {
      setSearchResults(data);
    },
  });

  /** 输入框change */
  const searchChange = (value: string) => {
    setSearchValue(value);

    if (value) {
      setShowContent(true);
      run(value);
    } else {
      setShowContent(false);
      setSearchResults(undefined);
    }
  };

  const handleToTargetAttachment = (id: string, type: TabEnum) => {
    setSearchId({ id, type });
    setActiveTab(type);
  };

  const isNoContent = useMemo(() => {
    return (
      Array.isArray(searchResults) ||
      isEmpty(searchResults) ||
      (searchResults?.coursewares.length === 0 &&
        searchResults?.tutorials.length === 0)
    );
  }, [searchResults]);

  const escapeRegExp = (string: string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  /** render waiting item */
  const renderCoursewareItem = (item: ISearchCoursewareType) => {
    const keyword = searchValue || "";

    // 转换关键词
    const simplifiedKeyword = tify(keyword);

    const traditionalKeyword = sify(keyword);

    // 创建正则表达式，忽略大小写并转义特殊字符
    const reg = new RegExp(
      `(${escapeRegExp(simplifiedKeyword)}|${escapeRegExp(
        traditionalKeyword
      )})`,
      "gi"
    );

    // 使用正则表达式高亮关键词
    const highlightKeyword = item.title.replace(
      reg,
      `<span style="color: #475AEC">$1</span>`
    );

    return (
      <div>
        <div
          className="text-base hover:bg-[#F8F8FF] mb-1"
          dangerouslySetInnerHTML={{ __html: highlightKeyword }}
        />
        <div className="bg-[#F8F8FF] rounded px-2 py-1.5 text-[#737489]">{`${t(
          "course"
        )}：${item.courseTitle}`}</div>
      </div>
    );
  };

  const renderTutorialItem = (item: ISearchTutorialType) => {
    const keyword = searchValue || "";

    // 转换关键词
    const simplifiedKeyword = tify(keyword);

    const traditionalKeyword = sify(keyword);

    // 创建正则表达式，忽略大小写并转义特殊字符
    const reg = new RegExp(
      `(${escapeRegExp(simplifiedKeyword)}|${escapeRegExp(
        traditionalKeyword
      )})`,
      "gi"
    );

    // 使用正则表达式高亮关键词
    const highlightKeyword = item.title.replace(
      reg,
      `<span style="color: #475AEC">$1</span>`
    );

    return (
      <div
        className="text-base"
        dangerouslySetInnerHTML={{ __html: highlightKeyword }}
      />
    );
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      dropdownRender={() => (
        <div className={styles.searchView}>
          <Input
            value={searchValue}
            placeholder={t("input_keyword_search") as string}
            suffix={
              <SearchOutlined
                className="text-sm"
                onClick={() => {
                  return;
                }}
              />
            }
            className={styles.searchInput}
            onChange={(event) => {
              searchChange(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                return;
              }
            }}
          />
          {showContent && (
            <div className={styles.searchResults}>
              {searchResults && !isNoContent ? (
                <div className={styles.searchItem}>
                  {!!searchResults.coursewares.length && (
                    <div
                      style={{
                        paddingBottom: 8,
                        borderBottom: "1px solid #EEEEF6",
                      }}
                    >
                      <div className={styles.searchCategory}>
                        {t("courseware_upload")}
                      </div>
                      {searchResults.coursewares.map((item) => (
                        <div
                          key={item.id}
                          className={styles.searchItemCourseware}
                          onClick={() =>
                            handleToTargetAttachment(item.id, TabEnum.Course)
                          }
                        >
                          <SearchOutlined className={styles.searchItemIcon} />
                          {renderCoursewareItem(item)}
                        </div>
                      ))}
                    </div>
                  )}
                  {!!searchResults.tutorials.length && (
                    <>
                      <div
                        className={styles.searchCategory}
                        style={{ marginTop: 16 }}
                      >
                        {t("tutorials")}
                      </div>
                      {searchResults.tutorials.map((item, index) => (
                        <div
                          key={index}
                          className={styles.searchItemTutorial}
                          onClick={() =>
                            handleToTargetAttachment(item.id, TabEnum.Tutorial)
                          }
                        >
                          <SearchOutlined className={styles.searchItemIcon} />
                          {renderTutorialItem(item)}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              ) : loading ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <LoadingOutlined />
                </div>
              ) : (
                <div>
                  <Divider className="mt-0" />
                  <div className="flex items-center flex-col">
                    <Icon icon="noSearchContent" width={80} height={80} />
                    <span className="mt-[25px]">{t("no_related_content")}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      placement="bottomRight"
      trigger={["click"]}
    >
      <span className={styles.searchBtn}>
        <Icon icon="searchIcon" />
      </span>
    </Dropdown>
  );
};
